import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * Represents a pending relationship item before the parent item is created.
 * Stored in React state until form submission.
 */
export interface PendingRelationship {
  /** Temporary UUID for tracking in the UI */
  id: string;
  /** Name of the relationship type (e.g., "Attendance Student Rel") */
  relationshipName: string;
  /** ID of the related item (e.g., Student ID) - optional for relationships without related items */
  relatedId?: string;
  /** Display data for the related item (name, itemNumber, etc.) */
  relatedItemData?: Record<string, unknown>;
  /** Properties on the relationship itself (e.g., comments, scheduledSession) */
  properties: Record<string, unknown>;
}

/**
 * Data structure for submitting relationships to the backend API
 */
export interface RelationshipSubmitData {
  relatedId?: string;
  properties: Record<string, unknown>;
}

interface PendingRelationshipsContextType {
  /** Map of pending relationships by relationship name */
  pendingRelationships: Map<string, PendingRelationship[]>;
  /** Add a new pending relationship */
  addPendingRelationship: (
    relationshipName: string,
    relatedId?: string,
    relatedItemData?: Record<string, unknown>,
    properties?: Record<string, unknown>,
  ) => string; // Returns the generated ID
  /** Update an existing pending relationship */
  updatePendingRelationship: (
    id: string,
    updates: Partial<
      Pick<PendingRelationship, "relatedId" | "relatedItemData" | "properties">
    >,
  ) => void;
  /** Remove a pending relationship by ID */
  removePendingRelationship: (id: string) => void;
  /** Get pending relationships for a specific relationship type */
  getPendingByRelationshipName: (
    relationshipName: string,
  ) => PendingRelationship[];
  /** Get all pending relationships formatted for API submission */
  getPendingRelationshipsForSubmit: () => Record<
    string,
    RelationshipSubmitData[]
  >;
  /** Get the count of pending relationships for a specific type */
  getPendingCount: (relationshipName: string) => number;
  /** Check if there are any pending relationships */
  hasPendingRelationships: boolean;
  /** Clear all pending relationships (call after successful submission) */
  clearAll: () => void;
}

const PendingRelationshipsContext =
  createContext<PendingRelationshipsContextType | null>(null);

interface PendingRelationshipsProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages pending relationships state.
 * Used in Create mode to collect relationships before the parent item is created.
 */
export const PendingRelationshipsProvider: React.FC<
  PendingRelationshipsProviderProps
> = ({ children }) => {
  const [pendingRelationships, setPendingRelationships] = useState<
    Map<string, PendingRelationship[]>
  >(new Map());

  const addPendingRelationship = useCallback(
    (
      relationshipName: string,
      relatedId?: string,
      relatedItemData?: Record<string, unknown>,
      properties: Record<string, unknown> = {},
    ): string => {
      const id = uuidv4();
      const newRelationship: PendingRelationship = {
        id,
        relationshipName,
        relatedId,
        relatedItemData,
        properties,
      };

      setPendingRelationships((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(relationshipName) || [];
        newMap.set(relationshipName, [...existing, newRelationship]);
        return newMap;
      });

      return id;
    },
    [],
  );

  const updatePendingRelationship = useCallback(
    (
      id: string,
      updates: Partial<
        Pick<
          PendingRelationship,
          "relatedId" | "relatedItemData" | "properties"
        >
      >,
    ) => {
      setPendingRelationships((prev) => {
        const newMap = new Map(prev);

        // Find and update the relationship
        for (const [relName, items] of newMap.entries()) {
          const index = items.findIndex((item) => item.id === id);
          if (index !== -1) {
            const updated = [...items];
            updated[index] = { ...updated[index], ...updates };
            newMap.set(relName, updated);
            break;
          }
        }

        return newMap;
      });
    },
    [],
  );

  const removePendingRelationship = useCallback((id: string) => {
    setPendingRelationships((prev) => {
      const newMap = new Map(prev);

      for (const [relName, items] of newMap.entries()) {
        const filtered = items.filter((item) => item.id !== id);
        if (filtered.length !== items.length) {
          if (filtered.length === 0) {
            newMap.delete(relName);
          } else {
            newMap.set(relName, filtered);
          }
          break;
        }
      }

      return newMap;
    });
  }, []);

  const getPendingByRelationshipName = useCallback(
    (relationshipName: string): PendingRelationship[] => {
      return pendingRelationships.get(relationshipName) || [];
    },
    [pendingRelationships],
  );

  const getPendingRelationshipsForSubmit = useCallback((): Record<
    string,
    RelationshipSubmitData[]
  > => {
    const result: Record<string, RelationshipSubmitData[]> = {};

    for (const [relName, items] of pendingRelationships.entries()) {
      result[relName] = items.map((item) => ({
        relatedId: item.relatedId,
        properties: item.properties,
      }));
    }

    return result;
  }, [pendingRelationships]);

  const getPendingCount = useCallback(
    (relationshipName: string): number => {
      return pendingRelationships.get(relationshipName)?.length || 0;
    },
    [pendingRelationships],
  );

  const hasPendingRelationships = useMemo(() => {
    for (const items of pendingRelationships.values()) {
      if (items.length > 0) return true;
    }
    return false;
  }, [pendingRelationships]);

  const clearAll = useCallback(() => {
    setPendingRelationships(new Map());
  }, []);

  const value = useMemo(
    () => ({
      pendingRelationships,
      addPendingRelationship,
      updatePendingRelationship,
      removePendingRelationship,
      getPendingByRelationshipName,
      getPendingRelationshipsForSubmit,
      getPendingCount,
      hasPendingRelationships,
      clearAll,
    }),
    [
      pendingRelationships,
      addPendingRelationship,
      updatePendingRelationship,
      removePendingRelationship,
      getPendingByRelationshipName,
      getPendingRelationshipsForSubmit,
      getPendingCount,
      hasPendingRelationships,
      clearAll,
    ],
  );

  return (
    <PendingRelationshipsContext.Provider value={value}>
      {children}
    </PendingRelationshipsContext.Provider>
  );
};

/**
 * Hook to access the pending relationships context.
 * Must be used within a PendingRelationshipsProvider.
 */
export const usePendingRelationships = (): PendingRelationshipsContextType => {
  const context = useContext(PendingRelationshipsContext);
  if (!context) {
    throw new Error(
      "usePendingRelationships must be used within a PendingRelationshipsProvider",
    );
  }
  return context;
};

export default PendingRelationshipsProvider;
